import * as React from 'react';
import { useStore } from 'react-redux';
import { DataGroupHeader } from '../../../components/data-group/DataGroupHeader';
import * as Forms from '../../../components/form-elements/_index';
import { useServiceCenter } from '../../../hooks/use-service-center';
import { serviceCenterSelectList } from '../../../selectors/userdata-selectors';
import { AppUtils } from '../../../utils/app-utils';
import { ContainerRead } from '../../models/containers/_index';
import { ContainerPaymentTypeMetadata } from '../../models/enums/container-payment';
import { ContainerSizeMetadata } from '../../models/enums/container-size';
import { carrierSelectList } from '../../selectors';
import { EditDeliveryBasicInfo } from './EditDeliveryBasicInfo';

interface IProps {
    container           : ContainerRead;
    onContainerChange   : any;
    showCompactUI       : any;
    allowCarrierSet     : boolean;
}

export const ContainerEditBasicInfo = (props: IProps) => {

    const store                             = useStore();
    const userServiceCenter                 = useServiceCenter();    
    const [editContainer, setEditContainer] = React.useState<ContainerRead>(new ContainerRead());
    
    const [serviceCenters] = React.useState(() => {
        return serviceCenterSelectList(store.getState());
    });
    
    const [carriers] = React.useState(() => {
        return carrierSelectList();
    });
    
    const [paymentOpts] = React.useState(() => {
        return AppUtils.enumToSelectList(ContainerPaymentTypeMetadata);
    });

    const [containerSizeOpts] = React.useState(() => {
        return AppUtils.enumToSelectList(ContainerSizeMetadata);
    });

    React.useEffect(() => {
        if (props.container) {    
            setEditContainer({...props.container});
        }
    }, [props.container]);

    /**
     * Service Center change handler
     */
    const onServiceCenterChange = (e: any) => {
        const t = {...editContainer} as ContainerRead;
        t.serviceCenterId =  e.target.value;
        setEditContainer(t);

        props.onContainerChange(t);
    };

    /**
     * Name change handler
     */
    const onNameChange = (e: any) => {
        const t = {...editContainer} as ContainerRead;
        t.name =  e.target.value;
        setEditContainer(t);

        props.onContainerChange(t);
    };

    /**
     * Update the state of the asset based on the name and 
     * value being passed in
     * @param e The event object from the input
     */
    const handleChange = (e: any) => {
        const t : any = { ...editContainer } as ContainerRead;

        const numericValues = ["carrierId", "paymentType", "size"]
        if(numericValues.indexOf(e.target.name) >= 0) {
            t[e.target.name] = parseInt(e.target.value, 10);
        }
        else {
            t[e.target.name] = e.target.value;
        }
        
        setEditContainer(t);

        props.onContainerChange(t);
    };

    const onCutoffDateChange = (newDate : Date) => {
        const t = {...editContainer} as ContainerRead;
        t.cutoffDate = newDate;
        setEditContainer(t);
        
        props.onContainerChange(t);
    };

    /**
     * Determine whether to disable the service center
     * dropdown
     */
    const shouldDisableServiceCenter = () => {  
        if (editContainer.id !== 0) {
            return true;
        }
        else if (userServiceCenter.name === 'ALL') {
            return false;
        }
        else {
            return true;
        }
    };

    const onNoteChange = (e : any) => {
        const t = {...editContainer} as ContainerRead;
        t.note = e.target.value;
        setEditContainer(t);
        props.onContainerChange(t);
    };

    return editContainer
            ? <React.Fragment>

                    <EditDeliveryBasicInfo
                        serviceCenter={ editContainer.serviceCenterId }
                        serviceCenters={ serviceCenters }
                        serviceCenterChange={ onServiceCenterChange }
                        name={ editContainer.name }
                        nameChange={ onNameChange }
                        cutoffDate={ editContainer.cutoffDate }
                        cutoffDateChange={ onCutoffDateChange }
                        showCompactUI={ props.showCompactUI }
                        disable={ false }
                        serviceCenterDisable={ shouldDisableServiceCenter() }
                    />

                    <div className="row">
                        <div className={ props.showCompactUI ? "col-md-12" : "col-md-4"}>
                            <Forms.TextInput
                                id="container-number"
                                type="text"
                                label="Number"
                                name="number"
                                onChange={ handleChange }
                                value={ editContainer.number } />
                        </div>
                    </div>

                    <div className="row">
                        <div className={ props.showCompactUI ? "col-md-12" : "col-md-4"}>
                            <Forms.TextArea 
                                id="container-note-textarea"
                                value={ editContainer.note }
                                label="Note"
                                defaultLines={3}
                                onChange={ onNoteChange }
                            />
                        </div>
                    </div>

                    <DataGroupHeader title="Carrier Information" />

                    <div className="row">
                        <div className={ props.showCompactUI ? "col-md-12" : "col-md-4"}>
                            <Forms.SelectInput
                                id="carrier"
                                label="Carrier"
                                name="carrierId"
                                disable={ !props.allowCarrierSet }
                                onChange={ handleChange }
                                options={ carriers }
                                value={ editContainer.carrierId } />
                        </div>
                    
                        <div className={ props.showCompactUI ? "col-md-12" : "col-md-4"}>
                            <Forms.SelectInput
                                id="container-paymentType"
                                label="Payment Type"
                                name="paymentType"
                                onChange={ handleChange }
                                options={ paymentOpts }
                                value={ editContainer.paymentType } />
                        </div>                
                    
                        <div className={ props.showCompactUI ? "col-md-12" : "col-md-4"}>
                            <Forms.SelectInput
                                id="container-size"
                                label="Container Size"
                                name="size"
                                onChange={ handleChange }
                                options={ containerSizeOpts }
                                value={ editContainer.size } />
                        </div>
                    </div>

            </React.Fragment>
            : null;
};
